import { Link } from 'react-router-dom';
import './App.css';

function Terms() {
  return (
    <main className="prose-page">
      <header className="prose-head">
        <span className="eyebrow">Legal</span>
        <h1 className="prose-title">
          Terms &amp; <span className="gradient-text">Conditions</span>
        </h1>
        <p className="prose-meta">Last updated: August 4, 2026</p>
      </header>

      <article className="prose">
        <section>
          <p>
            Please read these Terms and Conditions ("Terms") carefully before using the Noorvia
            mobile application ("App") operated by Mukuwa Baffoe ("we," "us," or "our").
          </p>
          <p className="callout">
            <strong>By downloading or using the App, you agree to be bound by these Terms.</strong>{' '}
            If you do not agree, do not use the App.
          </p>
        </section>

        <section>
          <h2>1. Use of the App</h2>
          <p>
            Noorvia is a personal Islamic companion app providing prayer time tracking, Quran
            reading, dhikr, fasting logs, notifications, and related features. You may use the App
            for personal, non-commercial purposes only.
          </p>
          <p>You agree not to:</p>
          <ul>
            <li>Reverse engineer, decompile, or disassemble the App</li>
            <li>Use the App for any unlawful purpose</li>
            <li>Attempt to gain unauthorized access to any portion of the App or its infrastructure</li>
          </ul>
        </section>

        <section>
          <h2>2. In-App Purchases</h2>
          <p>
            The App offers an optional paid subscription, Noorvia Gold, at $2.99 per month. All
            purchases are processed through Apple's App Store and are subject to Apple's payment
            terms. All sales are final. We do not issue refunds; refund requests must be directed to
            Apple.
          </p>
          <p>
            Noorvia Gold will auto-renew unless cancelled at least 24 hours before the end of the
            current billing period. You can manage or cancel your subscription in your Apple ID
            account settings.
          </p>
        </section>

        <section>
          <h2>3. iCloud &amp; Data Sync</h2>
          <p>
            The App uses iCloud (CloudKit) to sync your prayer logs, Quran progress, and other
            personal data across your devices. This sync requires an active iCloud account. We do
            not have access to your iCloud data; it is stored in your private iCloud container and
            governed by Apple's Privacy Policy.
          </p>
          <p>
            You are responsible for maintaining access to your iCloud account. We are not liable for
            data loss caused by iCloud service interruptions, account issues, or device changes.
          </p>
        </section>

        <section>
          <h2>4. Notifications</h2>
          <p>
            The App may send local notifications for prayer times, daily Quran verses, fasting
            reminders, and other alerts. You can manage notification permissions at any time through
            iOS Settings. We do not send marketing push notifications.
          </p>
        </section>

        <section>
          <h2>5. Location Data</h2>
          <p>
            The App uses your device's location solely to calculate accurate prayer times and Qibla
            direction. Location data is processed on-device and is never transmitted to our servers.
          </p>
        </section>

        <section>
          <h2>6. Accuracy of Religious Information</h2>
          <p>
            Prayer times, Quran text, Hadiths, and other Islamic content are provided for
            informational and personal use. While we strive for accuracy, we make no warranties
            regarding the completeness or correctness of any religious content. For matters of
            religious ruling (fatwa), please consult a qualified scholar.
          </p>
        </section>

        <section>
          <h2>7. Intellectual Property</h2>
          <p>
            All original content, design, code, and features of the App are the intellectual
            property of Mukuwa Baffoe. Quran text and Hadith content are sourced from publicly
            available translations and collections. You may not reproduce, distribute, or create
            derivative works from the App without prior written permission.
          </p>
        </section>

        <section>
          <h2>8. Third-Party Services</h2>
          <p>
            The App integrates with third-party services including Apple CloudKit, Apple StoreKit,
            WidgetKit, and prayer time calculation APIs. Your use of these services is subject to
            their respective terms and privacy policies. We are not responsible for the availability
            or conduct of third-party services.
          </p>
        </section>

        <section>
          <h2>9. Disclaimer of Warranties</h2>
          <p>
            The App is provided "as is" and "as available" without warranties of any kind, express
            or implied. We do not warrant that the App will be uninterrupted, error-free, or free of
            viruses or other harmful components. Your use of the App is at your sole risk.
          </p>
        </section>

        <section>
          <h2>10. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by law, we shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages arising from your use of the
            App, including but not limited to loss of data, loss of prayers logged, or device
            damage. Our total liability shall not exceed the amount you paid for the App or its
            in-app purchases in the twelve months preceding the claim.
          </p>
        </section>

        <section>
          <h2>11. Privacy</h2>
          <p>
            Your privacy is important to us. Our{' '}
            <Link to="/privacy-policy">Privacy Policy</Link>, which is incorporated into these Terms
            by reference, describes how we handle information. Please review it before using the
            App.
          </p>
        </section>

        <section>
          <h2>12. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. We will notify you of significant changes
            by updating the "Last updated" date. Continued use of the App after changes constitutes
            acceptance of the revised Terms.
          </p>
        </section>

        <section>
          <h2>13. Termination</h2>
          <p>
            We reserve the right to suspend or terminate your access to the App at our discretion,
            without notice, if you violate these Terms.
          </p>
        </section>

        <section>
          <h2>14. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Tennessee, United States of
            America, without regard to conflict of law principles. Any disputes arising from these
            Terms or your use of the App shall be resolved exclusively in the state or federal
            courts located in Nashville, Davidson County, Tennessee, and you consent to personal
            jurisdiction in those courts.
          </p>
        </section>

        <section>
          <h2>15. Contact</h2>
          <p>For questions about these Terms, contact us at:</p>
          <p className="contact">
            <a href="mailto:noorviasupport@gmail.com">noorviasupport@gmail.com</a>
          </p>
        </section>

        <section>
          <p className="callout">
            By using Noorvia, you acknowledge that you have read, understood, and agree to be bound
            by these Terms and Conditions.
          </p>
        </section>
      </article>
    </main>
  );
}

export default Terms;
